<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { Check, Search } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type SearchableOption = {
    value: string,
    label: string,
    description?: string,
};

function clampVisibleOptions(limit: number | null | undefined): number {
    if (typeof limit !== 'number' || !Number.isFinite(limit) || limit <= 0) return Number.POSITIVE_INFINITY;
    return Math.max(1, Math.floor(limit));
}

const props = withDefaults(defineProps<{
    id?: string,
    ariaLabel?: string,
    modelValue?: string,
    options?: ReadonlyArray<SearchableOption>,
    placeholder?: string,
    searchPlaceholder?: string,
    triggerClass?: string,
    variant?: 'default' | 'form',
    disabled?: boolean,
    allowClear?: boolean,
    clearLabel?: string,
    storageKey?: string,
    recentLimit?: number,
    visibleLimit?: number | null,
    emptyStateLabel?: string,
    showAllByDefault?: boolean,
}>(), {
    modelValue: '',
    id: undefined,
    ariaLabel: undefined,
    options: () => [],
    placeholder: 'Select option',
    searchPlaceholder: 'Search...',
    triggerClass: '',
    variant: 'default',
    disabled: false,
    allowClear: false,
    clearLabel: 'Clear',
    storageKey: '',
    recentLimit: 10,
    visibleLimit: 10,
    emptyStateLabel: 'Start typing to search.',
    showAllByDefault: false,
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
}>();

const open = ref(false);
const searchQuery = ref('');
const recentValues = ref<string[]>([]);

const selectedOption = computed(() => (
    props.options.find(option => option.value === props.modelValue) || null
));

const resolvedTriggerClass = computed(() => cn(
    props.variant === 'form'
        ? 'flex h-[var(--app-control-height)] w-full items-center rounded-md border border-input bg-background px-[var(--app-control-padding-x)] text-left text-sm'
        : 'w-full',
    props.triggerClass,
));

function sortByRecency(options: ReadonlyArray<SearchableOption>) {
    if (recentValues.value.length === 0) return options;
    const recencyIndex = new Map(recentValues.value.map((value, index) => [value, index] as const));

    return [...options].sort((a, b) => {
        const aIndex = recencyIndex.get(a.value);
        const bIndex = recencyIndex.get(b.value);
        const aHasRecency = typeof aIndex === 'number';
        const bHasRecency = typeof bIndex === 'number';
        if (aHasRecency && bHasRecency) return aIndex - bIndex;
        if (aHasRecency) return -1;
        if (bHasRecency) return 1;
        return a.label.localeCompare(b.label);
    });
}

const filteredOptions = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return [];

    return props.options.filter(option =>
        option.label.toLowerCase().includes(query)
        || option.value.toLowerCase().includes(query)
        || option.description?.toLowerCase().includes(query),
    );
});

const recentOptions = computed(() => {
    const seen = new Set<string>();
    const picked: SearchableOption[] = [];

    if (selectedOption.value) {
        seen.add(selectedOption.value.value);
        picked.push(selectedOption.value);
    }

    for (const value of recentValues.value) {
        const option = props.options.find(entry => entry.value === value);
        if (!option || seen.has(option.value)) continue;
        seen.add(option.value);
        picked.push(option);
        if (picked.length >= props.recentLimit) break;
    }

    return picked;
});

const maxVisibleOptions = computed(() => clampVisibleOptions(props.visibleLimit));

const visibleOptions = computed(() => {
    if (searchQuery.value.trim()) return sortByRecency(filteredOptions.value).slice(0, maxVisibleOptions.value);
    if (props.showAllByDefault) return sortByRecency(props.options).slice(0, maxVisibleOptions.value);

    const fallbackOptions = sortByRecency(props.options)
        .filter(option => !recentOptions.value.some(recent => recent.value === option.value));

    return [...recentOptions.value, ...fallbackOptions].slice(0, maxVisibleOptions.value);
});

const helperCopy = computed(() => (
    props.showAllByDefault
        ? 'Choose an option or search to narrow the list.'
        : 'Recent items stay visible here. Search when you need something else.'
));

function loadRecents() {
    if (!props.storageKey || !process.client) {
        recentValues.value = [];
        return;
    }

    try {
        const stored = window.localStorage.getItem(props.storageKey);
        const parsed = stored ? JSON.parse(stored) : [];
        recentValues.value = Array.isArray(parsed)
            ? parsed.map(value => String(value)).filter(Boolean)
            : [];
    } catch {
        recentValues.value = [];
    }
}

function persistRecent(value: string) {
    if (!props.storageKey || !process.client || !value) return;

    const next = [value, ...recentValues.value.filter(entry => entry !== value)].slice(0, Math.max(props.recentLimit, 8));
    recentValues.value = next;
    window.localStorage.setItem(props.storageKey, JSON.stringify(next));
}

function select(value: string) {
    emit('update:modelValue', value);
    persistRecent(value);
    open.value = false;
}

function clearSelection() {
    emit('update:modelValue', '');
    open.value = false;
}

onMounted(loadRecents);

watch(open, (isOpen) => {
    if (isOpen) {
        searchQuery.value = '';
        loadRecents();
    }
});
</script>

<template>
    <Popover v-model:open="open">
        <PopoverTrigger as-child>
            <button
                :id="id"
                data-slot="searchable-select-trigger"
                type="button"
                :aria-label="ariaLabel || placeholder"
                :disabled="disabled"
                :class="resolvedTriggerClass"
            >
                <slot name="trigger" :option="selectedOption">
                    <span class="text-sm" :class="selectedOption ? 'text-foreground' : 'text-muted-foreground'">
                        {{ selectedOption?.label || placeholder }}
                    </span>
                </slot>
            </button>
        </PopoverTrigger>

        <PopoverContent class="w-80 rounded-2xl border-border/70 p-3" align="start" @open-auto-focus.prevent>
            <div class="space-y-3">
                <div class="space-y-1">
                    <p class="text-sm font-medium">{{ placeholder }}</p>
                    <p class="text-xs text-muted-foreground">{{ helperCopy }}</p>
                </div>

                <div class="relative">
                    <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        v-model="searchQuery"
                        class="h-10 rounded-xl pl-9"
                        :placeholder="searchPlaceholder"
                    />
                </div>

                <div class="max-h-72 space-y-1 overflow-y-auto">
                    <button
                        v-if="allowClear"
                        type="button"
                        class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-muted/60"
                        @click="clearSelection"
                    >
                        <span class="text-sm text-muted-foreground">{{ clearLabel }}</span>
                        <Check v-if="!modelValue" class="h-4 w-4 text-primary" />
                    </button>

                    <button
                        v-for="option in visibleOptions"
                        :key="option.value"
                        type="button"
                        class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-muted/60"
                        @click="select(option.value)"
                    >
                        <div class="min-w-0">
                            <p class="truncate text-sm font-medium text-foreground">{{ option.label }}</p>
                            <p v-if="option.description" class="truncate text-xs text-muted-foreground">{{ option.description }}</p>
                        </div>
                        <Check v-if="modelValue === option.value" class="h-4 w-4 text-primary" />
                    </button>

                    <div
                        v-if="visibleOptions.length === 0"
                        class="rounded-xl border border-dashed border-border/70 px-3 py-4 text-sm text-muted-foreground"
                    >
                        {{ searchQuery.trim() ? 'No matches found.' : emptyStateLabel }}
                    </div>
                </div>
            </div>
        </PopoverContent>
    </Popover>
</template>
